import { ChevronRight } from "lucide-react";
import { ProfileCard } from "../../../types/profile";
import { getGenderPlaceholder } from "../../../helpers/matches.helper";
import { useCallback } from "react";

type DirectRegisteredProfilesProps = {
  profiles: ProfileCard[];
  totalCount: number;
  scrollerRef: any;
  title: string;
  takeProfileLabel: string;
  viewAllLabel?: string;
  onViewProfile: (profile: ProfileCard) => void;
  onTakeProfile: (profile: ProfileCard) => void;
  onViewAll?: () => void;
};

const DirectRegisteredProfiles = ({
  profiles,
  totalCount,
  scrollerRef,
  title,
  takeProfileLabel,
  viewAllLabel,
  onViewProfile,
  onTakeProfile,
  onViewAll,
}: DirectRegisteredProfilesProps) => {
  if (profiles.length === 0) {
    return null;
  }

  //   const openWhatsAppForSuperAdminProfile = useCallback(
  //     (profile: ProfileCard) => {
  //       const targetNumber = String(
  //         dashboardData?.supportWhatsAppNumber || "",
  //       ).replace(/\D/g, "");

  //       if (!targetNumber) {
  //         toast.error("Support WhatsApp number is not available.");
  //         return;
  //       }

  //       const message = [
  //         "I need this profile",
  //         `Profile ID: ${profile.maritalId || "N/A"}`,
  //         `Name: ${profile.name || "N/A"}`,
  //         `Bureau Name: ${bureauInfo?.bureauName || "N/A"}`,
  //         `Bureau Mobile Number: ${bureauInfo?.mobileNumber || "N/A"}`,
  //       ].join("\n");

  //       const whatsappUrl = `https://wa.me/${targetNumber}?text=${encodeURIComponent(
  //         message,
  //       )}`;

  //       window.location.href = whatsappUrl;
  //     },
  //     [
  //       dashboardData?.supportWhatsAppNumber,
  //       bureauInfo?.bureauName,
  //       bureauInfo?.mobileNumber,
  //     ],
  //   );

  return (
    <section
      data-dashboard-section="direct-registered-profiles"
      className="min-w-0 space-y-3"
    >
      {/* Header */}
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 ml-3">
          <h2 className="min-w-0 truncate text-sm font-semibold text-slate-900">
            {title}
          </h2>

          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
            {totalCount}
          </span>
        </div>

        {onViewAll ? (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/25 active:scale-95 md:bg-slate-100 md:text-slate-700 md:hover:bg-slate-200"
          >
            {viewAllLabel || "View All"}
            <ChevronRight size={14} />
          </button>
        ) : null}
      </div>

      {/* Horizontal cards */}
      <div
        ref={scrollerRef}
        className="-mx-4 min-w-0 overflow-x-auto px-4 pb-2 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-max min-w-full items-stretch gap-3">
          {profiles.map((profile) => {
            const profileKey = String(
              profile.recordId ||
                profile.id ||
                profile.maritalId ||
                profile.name,
            );

            const location = [profile.district, profile.state, profile.country]
              .filter(Boolean)
              .join(", ");

            const profileDetails = [
              profile.maritalStatus,
              profile.age !== undefined &&
              profile.age !== null &&
              profile.age !== ""
                ? `${profile.age} Yrs`
                : "",
              profile.height,
              profile.caste,
              profile.education,
              profile.occupation,
              profile.annualIncome ? `₹${profile.annualIncome}` : "",
              location,
            ]
              .filter(Boolean)
              .join(" • ");

            return (
              <article
                key={profileKey}
                className="
                  flex w-[calc(100vw-32px)] min-w-[280px] max-w-[420px]
                  flex-none flex-col overflow-hidden rounded-[20px] bg-white p-3
                  shadow-[0_10px_28px_rgba(15,23,42,0.08)]
                "
              >
                <button
                  type="button"
                  onClick={() => onViewProfile(profile)}
                  className="
                    grid w-full min-w-0 flex-1
                    grid-cols-[88px_minmax(0,1fr)] gap-3 text-left
                    min-[360px]:grid-cols-[100px_minmax(0,1fr)]
                    sm:grid-cols-[120px_minmax(0,1fr)]
                  "
                >
                  {/* Profile image */}
                  <div className="min-w-0 self-start">
                    <div className="relative aspect-square w-full overflow-hidden rounded-[16px] bg-slate-100">
                      <img
                        src={profile.image}
                        alt={profile.name || "Profile"}
                        loading="lazy"
                        className="h-full w-full object-cover object-top"
                        onError={(event) => {
                          event.currentTarget.src = getGenderPlaceholder("");
                        }}
                      />
                    </div>
                  </div>

                  {/* Profile details */}
                  <div className="min-w-0 self-start">
                    <div className="space-y-1.5">
                      <p className="line-clamp-2 break-words text-sm font-bold leading-5 text-slate-900">
                        {profile.name || ""}
                      </p>

                      {(profile.maritalId || profile.citizenship) && (
                        <p className="break-words text-[11px] font-semibold leading-4 text-violet-700">
                          {[
                            profile.maritalId,
                            profile.citizenship
                              ? `${profile.citizenship} Profile`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                      )}

                      {profileDetails ? (
                        <p className="line-clamp-6 break-words text-[11px] leading-[18px] text-slate-600">
                          {profileDetails}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </button>

                {/* Take Profile button */}
                {/* <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onTakeProfile(profile);
                  }}
                  className="
                    mt-3 inline-flex min-h-[42px] w-full shrink-0
                    items-center justify-center rounded-xl
                    bg-gradient-to-r from-violet-600 to-pink-600
                    px-3 py-2.5 text-center text-xs font-semibold leading-4
                    text-white transition hover:from-violet-700 hover:to-pink-700
                    active:scale-[0.99]
                  "
                >
                  {takeProfileLabel}
                </button> */}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DirectRegisteredProfiles;
